import { useState, useEffect, useCallback } from 'react'
import { useApi } from './useApi'
import { logger } from '../utils/logger'
import type { Customer } from '../types'

interface CustomerSearchResponse {
  items?: Customer[]
  customers?: Customer[]
}

interface CustomerSearchResult {
  searchQuery: string
  setSearchQuery: (query: string) => void
  customers: Customer[]
  isSearching: boolean
  error: string | null
  clearSearch: () => void
}

/**
 * 顧客検索カスタムフック
 * デバウンス処理とリアルタイム検索を提供
 */
export const useCustomerSearch = (): CustomerSearchResult => {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const api = useApi()

  // デバウンス処理（300ms）
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // 検索実行
  useEffect(() => {
    const searchCustomers = async () => {
      // 検索クエリが空の場合はスキップ
      if (!debouncedQuery.trim()) {
        setCustomers([])
        return
      }

      setIsSearching(true)
      setError(null)

      try {
        logger.debug('顧客検索開始:', { query: debouncedQuery })

        const params = new URLSearchParams({
          search: debouncedQuery.trim(),
          limit: '20', // 最大20件
        })

        const response = await api.get<CustomerSearchResponse>(`/api/customers?${params.toString()}`)

        setCustomers(response.items || response.customers || [])

        logger.debug('顧客検索完了:', {
          query: debouncedQuery,
          count: response.items?.length || response.customers?.length || 0
        })
      } catch (err) {
        logger.error('顧客検索エラー:', err)
        setError((err as Error).message || '顧客の検索に失敗しました')
        setCustomers([])
      } finally {
        setIsSearching(false)
      }
    }

    searchCustomers()
  }, [debouncedQuery, api])

  // 検索クエリをクリア
  const clearSearch = useCallback(() => {
    setSearchQuery('')
    setDebouncedQuery('')
    setCustomers([])
    setError(null)
  }, [])

  return {
    searchQuery,
    setSearchQuery,
    customers,
    isSearching,
    error,
    clearSearch,
  }
}
